import type { PanelMemberCoiSummary } from "../types/panel.type";
import type {
	EventGrantStatus,
	EventRole,
	PanelGrantRow,
} from "../types/eventGrant.type";

/**
 * Minimal structural shape a panel row must satisfy to map to a `PanelGrantRow`
 * — satisfied by BOTH `EvaluationPanelMember` (the `/evaluation/panel` index)
 * and `PanelMemberWithProgress` (the `/panel-members` status endpoint); both
 * serialise from the same `RfxEvaluationPanelMemberResource`.
 */
export interface MappablePanelMember {
	id: number;
	name: string | null;
	email: string;
	status: string;
	/** `PanelMemberWithProgress.role` — the scoring role. */
	role?: string;
	/** `EvaluationPanelMember.panel_role` — the seating role. */
	panel_role?: string;
	is_event_grant?: boolean;
	coi?: PanelMemberCoiSummary | null;
}

/**
 * Map a panel row (server-authoritative) to the `PanelGrantRow` the RBAC panel
 * surfaces consume. All decision-driving fields come straight from the server
 * (FF-W3-5): `is_event_grant` (principal identity — existence of a back-linked
 * grant, not liveness), `coi` ({status, rule}), and the panel-member `id` (the
 * COI PATCH / revoke target). The FE never infers grant identity or COI here
 * (C8) — it only reshapes.
 */

/**
 * Panel scoring statuses collapse to the grant lifecycle for DISPLAY: a member
 * who has opened/started/submitted is "accepted" (live) as far as the grant
 * badge is concerned. Terminal + not-yet-accepted states pass through.
 */
const toEventGrantStatus = (status: string): EventGrantStatus => {
	switch (status) {
		case "pending":
			return "pending";
		case "invited":
			return "invited";
		case "declined":
			return "declined";
		case "revoked":
			return "revoked";
		case "accepted":
		case "opened":
		case "in_progress":
		case "submitted":
		default:
			return "accepted";
	}
};

/**
 * Panel role → event-role label for display only (the authoritative
 * `event_role` lives on the grant, not the panel-members payload). The list
 * renders this as text; it does not drive any gate.
 */
const toEventRoleLabel = (role: string | undefined): EventRole => {
	switch (role) {
		case "observer":
			return "observer";
		case "rfxLead":
		case "member":
		default:
			return "panel_member";
	}
};

export const mapPanelMemberToGrantRow = (
	member: MappablePanelMember
): PanelGrantRow => ({
	id: member.id,
	name: member.name,
	email: member.email,
	event_role: toEventRoleLabel(member.role ?? member.panel_role),
	status: toEventGrantStatus(member.status),
	is_event_grant: member.is_event_grant ?? false,
	coi: member.coi ?? null,
	// The panel row id IS the RfxEvaluationPanelMember id — the COI PATCH /
	// revoke target. Only present (== id) for real panel members.
	panel_member_id: member.id,
});

export const mapPanelMembersToGrantRows = (
	members: MappablePanelMember[]
): PanelGrantRow[] => members.map(mapPanelMemberToGrantRow);
